// Release detail templates for info rows, listen dialogs, and tracklists.
import { t } from '../../../shared/config/translations'
import type { ClientRelease } from '../../../shared/types/collection'
import { localCalendarDate } from '../../../shared/utils/calendarDate'
import { escapeHtml } from '../../utils/html'
import { renderDateField } from '../inputs/dateFieldTemplates'
import { renderSelect } from '../inputs/selectTemplates'
import { renderSwitch } from '../inputs/switchTemplates'

export function listenedDialogTemplate(hideDurationOptions: string): string {
	const today = localCalendarDate()

	return `
    ${renderDateField({
		inputId: 'listen-date',
		label: t('release.listenedOn'),
		labelPlacement: 'outside',
		inputAttrs: {
			name: 'date',
			required: true,
			value: today,
			max: today,
		},
		inputClass: 'tt-input',
	})}
    ${renderSwitch({
		id: 'listen-unknown-date',
		label: t('release.unknownDate'),
		name: 'unknown-date',
	})}
    <div id="listen-date-error-container"></div>
    ${renderSelect({
		label: t('release.hideFor'),
		labelPlacement: 'outside',
		options: hideDurationOptions,
		selectId: 'hide-duration',
		inputClass: 'tt-input',
	})}
`
}

export function releaseInfoRowTemplate(
	label: string,
	value: string,
	options?: {
		copyValue?: string
		rowClassName?: string
		stacked?: boolean
		valueClassName?: string
	},
): string {
	const rowClassName = [
		'release-info-row',
		options?.stacked ? 'stacked' : '',
		options?.rowClassName,
	]
		.filter(Boolean)
		.join(' ')
	const valueClassName = [
		'release-info-value',
		options?.copyValue ? 'with-action' : '',
		value === t('release.noRecord') ? 'f secondary' : '',
		options?.valueClassName,
	]
		.filter(Boolean)
		.join(' ')
	const copyButton = options?.copyValue
		? `
            <button type="button" class="release-info-copy-button" data-copy-value="${escapeHtml(options.copyValue)}" title="Copy ${escapeHtml(label)}">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-copy-icon lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
            </button>
            <span class="release-info-copy-feedback" role="status" aria-live="polite"></span>
        `
		: ''

	return `
        <li class="${rowClassName}">
            <b class="release-info-label">${escapeHtml(label)}:</b>
            <span class="${valueClassName}"><span>${escapeHtml(value)}</span>${copyButton}</span>
        </li>
    `
}

export function releaseTracklistTemplate(data: ClientRelease['data']): string {
	if (!data.tracklist || data.tracklist.length === 0) return ''

	return data.tracklist
		.map((track, index, tracks) => {
			const prevSide =
				index > 0 ? getTrackSideKey(tracks[index - 1]?.position) : null
			const currentSide = getTrackSideKey(track.position)
			const sideChanged =
				!!prevSide && !!currentSide && prevSide !== currentSide
			const classes = sideChanged ? 'track side-break' : 'track'

			return `
                    <li class="${classes}">
                        <button
                            type="button"
                            class="track-preview-button"
                            data-preview-release-id="${data.id}"
                            data-preview-track-index="${index}"
                        >
                            <span class="f secondary">${escapeHtml(track.position)}</span>
                            <span class="track-title">${escapeHtml(track.title)}</span>
                            <span class="track-duration">
                                <span class="track-duration-text">${escapeHtml(track.duration)}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="track-playing-icon lucide lucide-audio-lines"><path d="M2 10v3"/><path d="M6 6v11"/><path d="M10 3v18"/><path d="M14 8v7"/><path d="M18 5v13"/><path d="M22 10v3"/></svg>
                            </span>
                        </button>
                    </li>
                `
		})
		.join('')
}

function getTrackSideKey(position?: string): string | null {
	if (!position) return null
	const normalized = position.trim().toUpperCase()
	if (!normalized) return null

	const tokenMatch = normalized.match(/^([A-Z0-9]+)[\s\-./]/)
	if (tokenMatch) return tokenMatch[1]

	const vinylMatch = normalized.match(/^([A-Z]+)/)
	return vinylMatch ? vinylMatch[1] : null
}
